import * as React from "react";
import {
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography,
  Button,
  Card as MuiCard,
  InputBase,
} from "@material-ui/core";
import { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { getEnrolledChannels, getProgramById, joinChannel, leaveChannel } from "../../../redux/actions/programAction";
import useStyles from "./style";
import appLogo from "../../../assets/logo.png";            // rename for clarity
import backIcon from "../../../assets/icons/backIcon.svg";
import AlertGreenIcon from "../../../components/Icons/AlertGreenIcon";
import HelpSupportIcon from "../../../components/Icons/HelpSupportIcon";
import { RootState } from "../../../redux/store";
import { Search } from "@material-ui/icons";
import { useSearchChannels } from "../../../util/hooks";
import { assetUrl } from "../../../util/assetUrl";         // NEW
import { usePrivileges } from "../../../util/usePrivileges";
// import appLogo from "../../../assets/logo.png";

/* ===== API/Asset bases ===== */                          // NEW
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:9090";
const ASSET_BASE = process.env.REACT_APP_ASSET_BASE_URL || API_BASE;

/* ===== Resolve stored/relative/absolute paths to usable URLs ===== */ // NEW
const resolveHeaderUrl = (p: string): string => {
  if (!p) return "";
  if (/^(blob:|data:|https?:\/\/)/i.test(p)) return p;
  if (p.startsWith("/assets") || p.startsWith("/uploads")) return `${ASSET_BASE}${p}`;
  try {
    return assetUrl(p);
  } catch {
    return p;
  }
};

interface ChannelScreenProps {
  handleBackPhase: () => void;
  selectedCommunity: string | null;
}

// THEME: readable text on secondaryColor
const invertColor = (hex: string) => {
  let col = (hex || "#ffffff").replace(/^#/, "");
  // fix this line in invertColor:
  if (col.length === 3) col = col.split("").map((c) => c + c).join("");
  const num = parseInt(col || "ffffff", 16);
  const r = 255 - ((num >> 16) & 0xff);
  const g = 255 - ((num >> 8) & 0xff);
  const b = 255 - (num & 0xff);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
};

function ChannelScreen({ handleBackPhase, selectedCommunity = null }: ChannelScreenProps) {
  console.log('🔵 ChannelScreen component mounting');

  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const { singleProgram, subscribeChannels } = useSelector((state: RootState) => state.programReducer);
  const activeCommunity = singleProgram?.communities.find(
    (cm) => cm?.communityIdentifier === selectedCommunity
  );
  const { privileges, communityEnrollment, communityVisibleChannelIds } = usePrivileges(selectedCommunity || undefined);

  console.log('🔍 Enrollment data from usePrivileges:', {
    communityEnrollment,
    enrollmentChannels: communityEnrollment?.channels,
    communityVisibleChannelIds,
  });

  // Track initialization state (resets on each mount, which is what we want)
  const initializedWithDataRef = useRef(false);

  // Load program data if not already loaded
  React.useEffect(() => {
    const loadProgramData = async () => {
      try {
        const activeProgramEnrollment = JSON.parse(localStorage.getItem("activeProgramEnrollment") || "{}");
        const programId = activeProgramEnrollment?.programId;

        if (programId && !singleProgram) {
          console.log('Loading program data for programId:', programId);
          await dispatch(getProgramById(programId) as any);
        }

        console.log('Program data load effect complete');
      } catch (error) {
        console.error('Error loading program data:', error);
      }
    };

    loadProgramData();
  }, []); // Run once on mount

  // ---- COMMUNITY THEME
  const communityTheme = {
    mainColor: activeCommunity?.communityStyle?.mainColor || "#1976d2",
    secondaryColor: activeCommunity?.communityStyle?.secondaryColor || "#ffffff",
    fontStyle: activeCommunity?.communityStyle?.fontStyle || "Arial",
  };
  const fgOnSecondary = invertColor(communityTheme.secondaryColor);

  // ---- COMMUNITY LOGO (TOP-RIGHT, cache-busted) -------------------------
  const communityLogoRaw = activeCommunity?.communityStyle?.logo || "";
  const [logoBust, setLogoBust] = React.useState(0);
  React.useEffect(() => {
    setLogoBust(Date.now());
  }, [communityLogoRaw, selectedCommunity, singleProgram?._id]);

  const communityLogoUrl = React.useMemo(() => {
    const u = resolveHeaderUrl(communityLogoRaw);
    if (!u) return "";
    return `${u}${u.includes("?") ? "&" : "?"}v=${logoBust}`;
  }, [communityLogoRaw, logoBust]);
  // -----------------------------------------------------------------------

  const communityChannels = activeCommunity?.channels || [];

  const viewableChannels = React.useMemo(
    () =>
      communityChannels.filter((channel: any) => {
        const channelId = channel?.channelIdentifier;
        if (!channelId) return false;
        if (!selectedCommunity) return true;

        // Include public channels regardless of explicit permissions
        const isPublic = channel?.channelPrivacySettings?.visibility !== 'private';
        if (isPublic) return true;

        // For private channels, check if user has explicit access
        return privileges.canViewChannel(selectedCommunity, channelId);
      }),
    [communityChannels, privileges, selectedCommunity]
  );

  const viewableSubscriptionIds = React.useMemo(() => {
    // Use communityEnrollment.channels to match the dashboard logic
    // This is the source of truth for what channels the user has enrolled in
    console.log('🔍 Computing viewableSubscriptionIds from communityEnrollment.channels:', {
      enrollmentChannels: communityEnrollment?.channels,
      isArray: Array.isArray(communityEnrollment?.channels),
      length: communityEnrollment?.channels?.length,
      selectedCommunity
    });

    const enrollmentChannels = (communityEnrollment?.channels as any[]) || [];

    if (!Array.isArray(enrollmentChannels)) {
      console.warn('⚠️ communityEnrollment.channels is not an array:', enrollmentChannels);
      return [];
    }

    // Filter by selected community and extract channel identifiers
    const rawIds = enrollmentChannels
      .filter((entry: any) => entry?.channelIdentifier && entry?.communityIdentifier === selectedCommunity)
      .map((entry: any) => entry.channelIdentifier)
      .filter(Boolean);

    console.log('🔍 Computed viewableSubscriptionIds:', rawIds);
    return rawIds as string[];
  }, [communityEnrollment, selectedCommunity]);

  const viewableSubscriptionSet = React.useMemo(
    () => new Set(viewableSubscriptionIds),
    [viewableSubscriptionIds]
  );

  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [checkedChannels, setCheckedChannels] = useState<string[]>([]);
  const [subscribeCheckedChannel, setSubscribeCheckedChannel] = useState<string[]>([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { setSearchQuery, filteredResults} = useSearchChannels(viewableChannels);
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Initialize subscription state when data loads (only once with actual data)
  React.useEffect(() => {
    console.log('=== INITIALIZATION CHECK ===', {
      alreadyInitialized: initializedWithDataRef.current,
      hasSingleProgram: !!singleProgram,
      viewableChannelsLength: viewableChannels.length,
      viewableSubscriptionIdsLength: viewableSubscriptionIds.length,
      hasCommunityEnrollment: !!communityEnrollment
    });

    if (initializedWithDataRef.current) {
      console.log('Already initialized with data via ref, skipping');
      return;
    }

    // Wait for program data to load first
    if (!singleProgram) {
      console.log('Program data not loaded yet, skipping initialization');
      return;
    }

    if (viewableChannels.length === 0) {
      console.log('No viewable channels yet, skipping initialization');
      return;
    }

    // Wait for communityEnrollment to be loaded from usePrivileges
    if (!communityEnrollment) {
      console.log('communityEnrollment not loaded yet, skipping initialization');
      return;
    }

    console.log('✅ INITIALIZING CHANNEL STATE with enrollment data:', {
      enrollmentChannels: communityEnrollment?.channels?.length || 0,
      viewableSubscriptionIds: viewableSubscriptionIds.length,
      viewableChannels: viewableChannels.length,
      enrolledChannelIds: viewableSubscriptionIds
    });

    setSubscribeCheckedChannel([...viewableSubscriptionIds]); // Create a copy to avoid reference issues
    const matched = viewableChannels
      .filter((item: any) => viewableSubscriptionIds.includes(item.channelIdentifier))
      .map((item: any) => item.channelIdentifier);
    setCheckedChannels([...matched]); // Create a copy

    console.log('✅ STATE INITIALIZED:', {
      subscribeCheckedChannel: viewableSubscriptionIds,
      checkedChannels: matched
    });

    // Mark as initialized - this prevents re-initialization when user changes checkboxes
    initializedWithDataRef.current = true;
  }, [singleProgram, communityEnrollment, viewableSubscriptionIds, viewableChannels]);

  const handleChange = (channelIdentifier: string) => {
    console.log('handleChange called for:', channelIdentifier);
    setCheckedChannels((prev) => {
      const willCheck = !prev.includes(channelIdentifier);
      console.log(`${willCheck ? 'Checking' : 'Unchecking'} ${channelIdentifier}, current checkedChannels:`, prev);
      return prev.includes(channelIdentifier)
        ? prev.filter((item) => item !== channelIdentifier)
        : [...prev, channelIdentifier];
    });
    setSelectedChannel(channelIdentifier);
  };

  const handleJoin = async () => {
    try {
      setIsLoading(true);
      const auth = JSON.parse(localStorage.getItem("authInfo") || "{}");
      const activeProgramEnrollment = JSON.parse(localStorage.getItem("activeProgramEnrollment") || "{}");

      const programId = activeProgramEnrollment?.programId;
      const userId = auth?._id;
      const communityId = activeProgramEnrollment?.communityIdentifier;
      const channelsToJoin = checkedChannels.filter((item) => !subscribeCheckedChannel.includes(item));
      const channelsToLeave = subscribeCheckedChannel.filter((item) => !checkedChannels.includes(item));

      console.log('=== CHANNEL OPERATION DEBUG ===');
      console.log('checkedChannels (what user wants):', checkedChannels);
      console.log('subscribeCheckedChannel (baseline):', subscribeCheckedChannel);
      console.log('channelsToJoin:', channelsToJoin);
      console.log('channelsToLeave:', channelsToLeave);

      if (channelsToJoin.length === 0 && channelsToLeave.length === 0) {
        console.error('❌ NO OPERATIONS TO PERFORM! Both arrays are empty!');
        console.error('This means checkedChannels === subscribeCheckedChannel');
        console.error('Either initialization failed or state was reset');
      } else {
        console.log(`✅ Will perform ${channelsToJoin.length} join(s) and ${channelsToLeave.length} leave(s)`);
      }
      console.log('===============================');

      // Execute all join operations sequentially
      for (const channel of channelsToJoin) {
        console.log(`Joining channel: ${channel}`);
        const result = await dispatch((joinChannel as any)(programId, userId, communityId, channel, false));
        console.log(`Join result for ${channel}:`, result);
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      // Execute all leave operations sequentially
      for (const channel of channelsToLeave) {
        console.log(`Leaving channel: ${channel}`);
        const result = await dispatch((leaveChannel as any)(programId, userId, communityId, channel, false));
        console.log(`Leave result for ${channel}:`, result);
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      console.log('All operations complete, refreshing enrolled channels...');

      // Refresh enrolled channels to get updated list
      await dispatch(getEnrolledChannels(programId, userId, communityId) as any);

      // Wait longer for Redux state to propagate
      await new Promise(resolve => setTimeout(resolve, 1500));

      console.log('Redirecting to dashboard...');

      // Now redirect to dashboard
      history.push("/user/dashboard");
    } catch (err) {
      console.error("Error in channel operations:", err);
      // Still redirect even if there's an error, but log it
      history.push("/user/dashboard");
    } finally {
      setIsLoading(false);
    }
  };

  return isLoading ? (
    <div className="loader-container">
      <div className="loader"></div>
    </div>
  ) : (
    <div
      className={classes.container}
      style={{
        backgroundColor: communityTheme.secondaryColor,
        minHeight: "100vh",
        padding: 12,
        fontFamily: communityTheme.fontStyle,
      }}
    >
      <MuiCard
        className={classes.card}
        ref={cardRef}
        style={{
          backgroundColor: communityTheme.secondaryColor,
          color: fgOnSecondary,
          border: `1px solid ${fgOnSecondary}33`,
          boxShadow: `0 8px 24px ${fgOnSecondary}22`,
          paddingBottom: 24,
        }}
      >
        {/* THEMED HEADER BAR */}
        <Box
          className={classes.header}
          style={{
            //position: "relative",          // allow absolute right logo
            backgroundColor: communityTheme.mainColor,
            color: communityTheme.secondaryColor,
          }}
        >
          <img src={backIcon} alt="Back" className={classes.backIcon} onClick={handleBackPhase} />

          {/* TOP-RIGHT custom community logo (fallback to app logo) */}
          <Box className={classes.logoWrapper}>
            <img
              src={communityLogoUrl || resolveHeaderUrl(appLogo)}
              alt="Community Logo"
              className={classes.logo}
              style={{
                width: "auto",               // a bit wider than before so it reads well
                height: 28,
                objectFit: "contain",
                display: "block"
               // filter: "brightness(0) invert(1)", // white-on-color
              }}
            />
          </Box>
        </Box>

        {/* TITLES */}
        <Typography className={classes.titleCon}>
          <h1 className={classes.onboardingTitle} style={{ color: fgOnSecondary }}>Onboarding</h1>
        </Typography>
        <Typography className={classes.titleHeading}>
          <h1 className={classes.onboardingHeading} style={{ color: communityTheme.mainColor }}>
            Choose Channels
          </h1>
        </Typography>

        {/* SEARCH */}
        <div
          className={classes.searchInput}
          style={{
            border: `1px solid ${fgOnSecondary}33`,
            backgroundColor: `${fgOnSecondary}0D`,
          }}
        >
          <InputBase
            placeholder="Search"
            fullWidth
            startAdornment={<Search style={{ marginRight: 8, cursor: "pointer", color: `${fgOnSecondary}99` }} />}
            onChange={(e) => setSearchQuery(e.target.value)}
            inputProps={{ style: { color: fgOnSecondary } }}
          />
        </div>

        {/* LIST */}
        {filteredResults.length > 0 && (
          <List className={classes.communityListWrapper} style={{ backgroundColor: "transparent" }}>
            {filteredResults.map((channel: any, index: number) => (
              <ListItem
                key={index}
                button
                onClick={() => handleChange(channel.channelIdentifier)}
                style={{
                  backgroundColor: `${fgOnSecondary}0D`,
                  borderRadius: 10,
                  marginBottom: 6,
                  border: `1px solid ${fgOnSecondary}22`,
                }}
              >
                <HelpSupportIcon width={25} height={25} />
                <ListItemText
                  primary={channel.channelName}
                  style={{ marginLeft: 20, color: fgOnSecondary }}
                />
                <Checkbox
                  checked={checkedChannels.includes(channel.channelIdentifier)}
                  icon={<span className={classes.checkBoxIcon} />}
                  checkedIcon={
                    <span className={classes.checkedIcon}>
                      <svg
                        viewBox="0 0 24 24"
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: 14,
                          height: 14,
                          stroke: communityTheme.mainColor,
                          strokeWidth: 2,
                          fill: "none",
                        }}
                      >
                        <path d="M5 12l5 5L20 7" />
                      </svg>
                    </span>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}

        {/* PRIMARY ACTION */}
        <Button
          fullWidth
          size="small"
          className={classes.submitBtn}
          onClick={handleJoin}
          style={{
            backgroundColor: communityTheme.mainColor,
            color: communityTheme.secondaryColor,
            borderRadius: 12,
            fontWeight: 600,
          }}
        >
          Next
        </Button>

        {/* Logout Button */}
        <Button
          fullWidth
          size="small"
          style={{
            marginTop: '12px',
            color: fgOnSecondary + '99',
            textTransform: 'none',
            fontSize: '14px',
            borderRadius: 12,
          }}
          onClick={() => {
            localStorage.clear();
            window.location.href = "/login";
          }}
        >
          Logout
        </Button>

        {/* Popover */}
        <Popover
          open={popoverOpen}
          anchorReference="anchorPosition"
          anchorPosition={{ top: window.innerHeight / 2, left: window.innerWidth / 2 }}
          onClose={() => setPopoverOpen(false)}
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          className={classes.popover}
        >
          <Box p={2} textAlign="center" className={classes.alertIconWrapper}>
            <AlertGreenIcon width={25} height={25} />
            <Box className={classes.alertHeadingwrapper}>
              <Typography style={{ color: communityTheme.mainColor, fontWeight: 700 }}>
                {selectedChannel}
              </Typography>
              <Typography className="subtext" style={{ fontFamily: "Arial", fontSize: 12, color: "#00000066" }}>
                Protecting Communities, Ensuring Safety.
              </Typography>
            </Box>
          </Box>

          <Typography variant="body2" color="textSecondary">
            {`The ${selectedChannel} Channel keeps you informed with real-time alerts, emergency updates, and safety tips to protect your community.`}
          </Typography>
        </Popover>
      </MuiCard>
    </div>
  );
}
export default ChannelScreen;
